import { useEffect, useRef, useState } from 'react';

/** beforeinstallprompt is supported by Chromium, not Safari or every browser. */
type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};
type InstallSurface = 'none' | 'native' | 'ios' | 'android' | 'desktop-help';

const DISMISS_UNTIL_KEY = 'hopper.pwa.install.dismissUntil';
const DISMISS_DELAY_MS = 7 * 24 * 60 * 60 * 1000;

function installed(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function iosDevice(): boolean {
  return /iPad|iPhone|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function androidDevice(): boolean {
  return /Android/i.test(navigator.userAgent);
}

function iosSafari(): boolean {
  return /Safari/i.test(navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(navigator.userAgent);
}

function dismissedRecently(): boolean {
  try {
    const deadline = Number(localStorage.getItem(DISMISS_UNTIL_KEY) ?? '0');
    return Number.isFinite(deadline) && deadline > Date.now();
  } catch {
    return false;
  }
}

function postpone(milliseconds = DISMISS_DELAY_MS): void {
  try {
    localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + milliseconds));
  } catch {
    // Private/restricted browsers may not permit local storage.
  }
}

function initialSurface(): InstallSurface {
  if (!import.meta.env.PROD || installed() || dismissedRecently()) return 'none';
  if (iosDevice()) return 'ios';
  if (androidDevice()) return 'android';
  return 'none';
}

/** Consumer-only install guidance. Native prompt requires a real user gesture. */
export function PwaInstallPrompt({ suspended }: { suspended: boolean }) {
  const [surface, setSurface] = useState<InstallSurface>(initialSurface);
  const [showSteps, setShowSteps] = useState(false);
  const [busy, setBusy] = useState(false);
  const deferredPrompt = useRef<InstallPromptEvent | null>(null);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const onPrompt = (event: Event) => {
      if (installed()) return;
      event.preventDefault();
      if (dismissedRecently()) return;
      deferredPrompt.current = event as InstallPromptEvent;
      setSurface('native');
      setShowSteps(false);
    };
    const onInstalled = () => {
      deferredPrompt.current = null;
      postpone(30 * 24 * 60 * 60 * 1000);
      setSurface('none');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    deferredPrompt.current = null;
    postpone();
    setSurface('none');
  };

  const requestInstall = async () => {
    const prompt = deferredPrompt.current;
    if (!prompt || busy) return;
    deferredPrompt.current = null;
    setBusy(true);
    try {
      await prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === 'accepted') {
        setSurface('none');
      } else {
        dismiss();
      }
    } catch {
      // A browser can withdraw the prompt. Offer instructions, not fake success.
      setSurface(androidDevice() ? 'android' : 'desktop-help');
      setShowSteps(true);
    } finally {
      setBusy(false);
    }
  };

  if (suspended || surface === 'none' || installed()) return null;

  const iosSteps = [
    ...(!iosSafari() ? ['Откройте сайт gohopper.ru в Safari.'] : []),
    'Нажмите кнопку с тремя точками («…») в Safari.',
    'В открывшемся меню выберите «Поделиться».',
    'Нажмите «Показать больше».',
    'Выберите «Добавить на экран Домой».',
    'Нажмите «Добавить», чтобы завершить установку.',
  ];
  const instructions = surface === 'android'
    ? 'В Chrome нажмите меню ⋮ → «Установить приложение» или «Добавить на главный экран».'
    : 'В адресной строке Chrome или Edge выберите значок установки приложения.';

  return (
    <aside className="pwa-update-notice pwa-install-notice" role="region" aria-label="Установка Хоппера">
      <div className="pwa-install-notice__heading">
        <img src="/icons/hopper-192.png" alt="" width="44" height="44" />
        <div>
          <strong className="pwa-update-notice__title">Хоппер всегда под рукой</strong>
          <p>Установите Хоппер на устройство и открывайте одним касанием.</p>
        </div>
      </div>
      {showSteps && (surface === 'ios'
        ? (
          <ol className="pwa-install-notice__steps pwa-install-notice__step-list">
            {iosSteps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        )
        : <p className="pwa-install-notice__steps">{instructions}</p>
      )}
      <div className="pwa-update-notice__actions">
        {surface === 'native' ? (
          <button type="button" disabled={busy} className="pwa-update-notice__update" onClick={() => void requestInstall()}>
            Установить
          </button>
        ) : (
          <button type="button" className="pwa-update-notice__update" onClick={() => setShowSteps(true)}>
            Как установить
          </button>
        )}
        <button type="button" className="pwa-update-notice__later" onClick={dismiss}>Позже</button>
      </div>
    </aside>
  );
}
